import React, { useEffect, useState } from "react";
import { Sparkles, Target, ShieldCheck, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Ticket } from "lucide-react";
import { pickModuleAccess } from "@/lib/moduleAccess";

export default function ParentConsultancy() {
  const [tab, setTab] = useState("LGS");
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [history, setHistory] = useState([]);
  const [credits, setCredits] = useState(0);
  // Abonelik (unlimited/aylık kota) varsa öneri kredi istemeden alınır.
  const [access, setAccess] = useState({ free: false, cost: 3 });

  const loadCredits = () => api.get(`${V1}/users/me/credits`).then((r) => setCredits(r.data?.exam_credits ?? 0)).catch(() => {});
  const loadAccess = () => api.get(`${V1}/users/me/usage`).then((r) => setAccess(pickModuleAccess(r.data, "tercih_danismanligi"))).catch(() => {});

  useEffect(() => {
    api.get(`${V1}/children`).then((r) => setChildren(r.data || [])).catch(() => setChildren([]));
    api.get(`${V1}/ai/preference-consultancy/mine`).then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
    loadCredits();
    loadAccess();
  }, []);

  const submit = async () => {
    if (!access.free && credits < access.cost) {
      toast.error(`Tercih danışmanlığı ${access.cost} kredi. Bakiyeniz: ${credits}`);
      return;
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`${V1}/ai/preference-consultancy`, {
        exam_type: tab, child_id: childId || null, payload: form,
      });
      setResult(data);
      const h = await api.get(`${V1}/ai/preference-consultancy/mine`);
      setHistory(h.data || []);
      if (access.free) {
        toast.success("AI önerileriniz hazır! Aboneliğinize dahil.");
        loadAccess();
      } else {
        loadCredits();
        toast.success(`AI önerileriniz hazır! ${access.cost} kredi kullanıldı.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Öneri oluşturulamadı");
    } finally { setBusy(false); }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-8" data-testid="parent-consultancy-page">
      <div>
        <div className="overline text-accent">Yapay Zekâ Destekli</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Tercih Danışmanlığı</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Puanlarınıza göre AI, üç kategoride öneri sunar. <b>Uyarı:</b> öneriler geçmiş verilere dayalı tahmindir; yerleştirme garanti edilmez.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4">
          <Label>Öğrenci (opsiyonel)</Label>
          <Select value={childId} onValueChange={setChildId}>
            <SelectTrigger data-testid="cons-child-select"><SelectValue placeholder="Bir çocuk seç" /></SelectTrigger>
            <SelectContent>
              {children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.surname}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={tab} onValueChange={(t) => { setTab(t); setForm({}); }}>
          <TabsList>
            <TabsTrigger value="LGS" data-testid="cons-tab-lgs">LGS</TabsTrigger>
            <TabsTrigger value="YKS" data-testid="cons-tab-yks">YKS</TabsTrigger>
          </TabsList>
          <TabsContent value="LGS">
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <Field label="LGS Puanı" onChange={(v) => setF("lgs_score", parseFloat(v))} testid="lgs-score" />
              <Field label="Yüzdelik Dilim" onChange={(v) => setF("percentile", parseFloat(v))} testid="lgs-percentile" />
              <Field label="OBP (Diploma)" onChange={(v) => setF("diploma_score", parseFloat(v))} testid="lgs-diploma" />
              <Field label="Tercih Şehri" onChange={(v) => setF("preferred_city", v)} type="text" testid="lgs-city" />
              <Field label="Okul Türü" onChange={(v) => setF("school_type", v)} type="text" placeholder="Anadolu / Fen / MTAL vb." testid="lgs-type" />
              <Field label="Beklenen Burs (%)" onChange={(v) => setF("scholarship_pct", parseInt(v))} testid="lgs-schol" />
            </div>
          </TabsContent>
          <TabsContent value="YKS">
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <Field label="TYT Puanı" onChange={(v) => setF("tyt_score", parseFloat(v))} testid="yks-tyt" />
              <Field label="AYT Puanı" onChange={(v) => setF("ayt_score", parseFloat(v))} testid="yks-ayt" />
              <Field label="Yerleştirme Puanı" onChange={(v) => setF("placement", parseFloat(v))} testid="yks-place" />
              <Field label="Sıralama" onChange={(v) => setF("ranking", parseInt(v))} testid="yks-rank" />
              <Field label="Puan Türü" onChange={(v) => setF("score_type", v)} type="text" placeholder="Sayısal / Sözel / EA / Dil" testid="yks-type" />
              <Field label="Hedef Bölüm" onChange={(v) => setF("target_department", v)} type="text" testid="yks-dept" />
            </div>
          </TabsContent>
        </Tabs>
        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <Button onClick={submit} disabled={busy || (!access.free && credits < access.cost)} size="lg" data-testid="cons-submit">
            <Sparkles className="h-4 w-4" />
            {busy ? "Öneriler hazırlanıyor…" : access.free ? "Öneri Al (Aboneliğine Dahil)" : `Öneri Al (${access.cost} Kredi)`}
          </Button>
          <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent/10 text-accent" data-testid="cons-credits-chip">
              <Ticket className="h-3 w-3" /> Krediniz: <b>{credits}</b>
            </span>
            {!access.free && credits < access.cost && (
              <Link to="/panel/referans" className="text-primary underline text-xs" data-testid="cons-earn-credits-link">Kredi kazan →</Link>
            )}
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-6">
          <div className="p-6 rounded-xl border border-accent/30 bg-accent/5">
            <div className="overline text-accent mb-2">Özet</div>
            <div className="text-sm leading-relaxed">{result.ai_summary}</div>
          </div>
          {["reach", "realistic", "safe"].map((cat) => {
            const items = (result.recommendations || []).filter((r) => r.category === cat);
            if (items.length === 0) return null;
            const meta = {
              reach: { title: "Zorlayıcı (Reach)", icon: TrendingUp, tone: "text-accent" },
              realistic: { title: "Gerçekçi (Realistic)", icon: Target, tone: "text-primary" },
              safe: { title: "Güvenli (Safe)", icon: ShieldCheck, tone: "text-success" },
            }[cat];
            return (
              <div key={cat} className="rounded-xl border border-border bg-card p-6" data-testid={`cons-cat-${cat}`}>
                <div className={`flex items-center gap-2 mb-4 ${meta.tone}`}>
                  <meta.icon className="h-5 w-5" />
                  <div className="font-display font-bold">{meta.title}</div>
                </div>
                <div className="grid gap-3">
                  {items.map((r, i) => (
                    <div key={i} className="p-4 rounded border border-border">
                      <div className="font-semibold">{r.school || r.department} <span className="text-muted-foreground text-sm">— {r.city}</span></div>
                      {r.department && r.school && <div className="text-sm text-muted-foreground">{r.department}</div>}
                      <div className="text-xs mt-1 text-muted-foreground">{r.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="font-display font-bold text-lg mb-3">Önceki Talepleriniz</div>
          <div className="space-y-2 text-sm">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex justify-between border-b border-border py-2">
                <span>{h.exam_type} · {new Date(h.created_at).toLocaleDateString("tr-TR")}</span>
                <span className="text-muted-foreground">{(h.recommendations || []).length} öneri</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, onChange, type = "number", placeholder, testid }) => (
  <div>
    <Label>{label}</Label>
    <Input type={type} step="any" placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);
